import React, { useEffect, useMemo, useState } from "react";
import {
    Navigate,
    Route,
    Routes,
} from "react-router-dom";
import jwtDecode from "jwt-decode";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { GET_BOOKING_TYPES, GET_TOKEN } from "./graphql/queries";
import BusinessPortal from "./pages/business/businessPage/businessPortal";
import CustomerHome from "./pages/customer/home";
import CategoryPage from "./pages/customer/categoryPage";
import BusinessLandingPage from "./pages/business/businessPage/businessLandingPage";
import PricingPage from "./pages/business/pricingPage";
import ForgotPassword from "./components/auth/forgotPassword";
import Calendar from "./pages/business/calendarPage/calendar";
import { CalendarProvider } from "./components/business/calendar/calendarContext";
import PrintPortal from "./pages/print/pintPortal";
import EmbeddedBooking from "./pages/business/embed/booking/embeddedBooking";
import UpdateStaffStatus from "./components/business/staff/updateStaffStatus";
import BookingTypePage from "./pages/business/bookingTypePage/bookingTypePage";
import ValidateBusinessUrl from "./util/validateBusinessId";
import UserBookingTypePage from "./pages/business/userPage/userBookingTypePage";
import BookingPage from "./pages/customer/booking/bookingPage";
import ConfirmPage from "./pages/customer/booking/confirmPage";
import SignUp from "./components/auth/signup";
import PrivacyTermsAbout from "./pages/privacy/privacyTermsAbout";
import Main from "./components/common/main";
import IntegrationHeader from "./components/integrations/integrationHeader";
import Integration from "./components/integrations/integration";
import UserProfilePage from "./pages/userPortal/userPortal";
import SubscriptionSuccessPage from "./pages/business/subscriptionSuccessPage";
import StaffPortal from "./pages/business/staffPage/staffPortal";
import IntegrationPage from "./components/integrations/integrationPage";
import CreateNewDashBoard from "./components/business/bookingType/createNewDashBoard";
import BookingTypeEntryForm from "./components/business/bookingType/bookingTypeEntryForm";
import BookingTypeHomePortal from "./pages/business/bookingTypePage/bookingTypeHomePortal";
import Error from "./components/common/error";
import SearchPage from "./components/consumer/search/searchPage";
import Login from "./components/auth/login/login";
import BusinessBookingTypes from "./components/business/bookingType/businessBookingTypes";
import ScheduledBookingType from "./components/business/bookingType/scheduledBookingType";
import TipPage from "./components/tip/tipPage";
import { BuyCoinsPage } from "./components/tip/buyCoinsPage";
import WalletDashboard from "./components/tip/walletDashboard";
import WithdrawPage from "./components/tip/withdrawPage";
import UserPage from "./pages/business/userPage/userPage";
import { TipProvider } from "./components/tip/tipProvider";
import { useSelectedBusiness } from "./redux/store"; 
const RouteGroups = ({ providerUser, setProviderUser, token, login, logout, setImage }) => {
    const location = useLocation();
    const [myBookingTypes, setMyBookingTypes] = useState(null);
    const selectedBusiness = useSelectedBusiness();

    const {
        loading: myBookingTypesLoading,
        error: myBookingTypesError,
        data: myBookingTypesData,
        refetch: myBookingTypesDataRefetch,
    } = useQuery(GET_BOOKING_TYPES, {
        variables: { staffId: Number(providerUser?.staffId) ?? -1 },
        skip: !providerUser?.staffId // Skip if staffId is not available
    });

    const {
        loading: providerUserLoading,
        error: providerUserError,
        data: providerUserData,
        refetch: tokenRefetch,
    } = useQuery(GET_TOKEN, {
        variables: { staffId: providerUser?.staffId ?? providerUser.userId },
        skip: !providerUser?.staffId // Skip if staffId is not available
    });



    useEffect(() => {
        if (!myBookingTypesLoading && !myBookingTypesError && myBookingTypesData) {
            setMyBookingTypes(myBookingTypesData?.getBookingTypes);
        }
    }, [myBookingTypesLoading, myBookingTypesError, myBookingTypesData]);



    useEffect(() => {
        if (!providerUserLoading && !providerUserError && providerUserData) {

            const providerUser =
                providerUserData && providerUserData !== "undefined"
                    ? jwtDecode(providerUserData.getToken.token)
                    : null;
            setProviderUser(providerUser);
        }
    }, [providerUserLoading, providerUserError, providerUserData]);


    const memoizedBookingTypes = React.useMemo(() => myBookingTypes, [myBookingTypes]);


    const hasLoading = useMemo(() => {
        return myBookingTypesLoading || providerUserLoading;
    }, [myBookingTypesLoading, providerUserLoading]);

    const hasError = useMemo(() => {
        return myBookingTypesError || providerUserError;
    }, [myBookingTypesError, providerUserError]);


    const ProtectedComponent = ({ children }) => {
        const location = useLocation();

        // 1️⃣ If they’re not logged in at all, just bounce to /login
        if (!providerUser.userId) {
            localStorage.setItem(
                "redirectAfterLogin",
                location.pathname + location.search
            );
            return <Navigate to="/login" replace />;
        }

        // 2️⃣ If they *are* logged in but have no business yet,
        //    save where they were headed, then send them to the business form
        if (
            !providerUser?.defaultBusinessId &&
            location.pathname !== '/biz/business'
        ) {
            if (!localStorage.getItem("redirectAfterBusiness")) {
                const loginRedirect = localStorage.getItem("redirectAfterLogin");
                const intended = loginRedirect || location.pathname + location.search;
                localStorage.setItem("redirectAfterBusiness", intended);
            }
            return <Navigate to="/biz/business" replace />;
        }

        // 3️⃣ Otherwise, they’re good to go
        return <>{children}</>;
    };


    const ProtectedComponentNeedToHaveBusiness = ({ children }) => {

        if (!token) {
            localStorage.setItem(
                "redirectAfterLogin",
                location.pathname + location.search
            );

            return <Navigate to="/login" replace />;
        }


        if (
            !providerUser?.defaultBusinessId &&
            location.pathname !== "/biz/business"
        ) {
            return <Navigate to="/biz/business" replace />;
        }

        return children;
    };


    const NotFound = () => <Error />;
    // Render loading state


    // Render error state
    const renderError = () => (
        <Typography variant="h6" color="error" align="center">
            An error occurred while loading the data. Please try again.
        </Typography>
    );


    const renderLoading = () => {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <h2>Loading...</h2>
                <p>Please wait while we fetch the data.</p>
                {/* You can use a spinner or loading icon here */}
                <div className="spinner"></div> {/* Example loading spinner */}
            </div>
        );
    };
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID; // Access the environment variable

    // 1️⃣ Compute redirect target AFTER login, but don't clear yet
    const postLoginRedirect = useMemo(() => {
        return localStorage.getItem("redirectAfterLogin") ?? "/biz/bookingtypes";
    }, [token]);

    const routes = useMemo(() => (
        <Routes>
            <Route path="/" element={<CustomerHome logout={logout} />} />
            <Route path='/category/:category' element={<CategoryPage />} />
            <Route path="/biz" element={<BusinessLandingPage />} />
            <Route
                path="/:businessUrl/:staffUrl/withdraw"
                element={<ProtectedComponent>
                    <PayPalScriptProvider options={{
                        "client-id": clientId,
                    }}>
                        <TipProvider><WithdrawPage /></TipProvider>
                    </PayPalScriptProvider>
                </ProtectedComponent>}
            />
            <Route path="biz/pricing" element={
                <PayPalScriptProvider options={{
                    "client-id": clientId,
                    vault: true,
                    intent: "subscription"
                }}>
                    <PricingPage />
                </PayPalScriptProvider>
            } />
            <Route
                path="/login"
                element={
                    !token ? (
                        <Login token={token} login={login} />
                    ) : (
                            <Navigate to={postLoginRedirect} replace />
                    )
                }
            />
            <Route
                path="/start-free-trial"
                element={
                    !token ? (
                        <SignUp token={token} login={login} />
                    ) : (
                            <Navigate to={postLoginRedirect} replace />
                    )
                }
            />
            <Route
                path="/forgotpassword"
                element={
                    !token ? (
                        <ForgotPassword token={token} login={login} />
                    ) : (
                            <Navigate to={postLoginRedirect} replace />
                    )
                }
            />

            <Route
                path="/biz/business/:tabId?"
                element={
                    <ProtectedComponent>
                        <PayPalScriptProvider options={{
                            "client-id": clientId,
                            vault: true,
                            intent: "subscription"
                        }}>
                            <BusinessPortal />
                        </PayPalScriptProvider>
                    </ProtectedComponent >

                }
            />
            <Route
                path="/biz/calendar"
                element={
                    <ProtectedComponent>
                        <Calendar myBookingTypes={memoizedBookingTypes} />
                    </ProtectedComponent>
                }
            />
            <Route path="/:businessUrl/:staffUrl/wallet" element={<ProtectedComponent><TipProvider><WalletDashboard /></TipProvider></ProtectedComponent>} />
            <Route
                path="/biz/printview"
                element={
                    <ProtectedComponent>
                        <PrintPortal myBookingTypes={memoizedBookingTypes} />
                    </ProtectedComponent>
                }
            />

            <Route path="/:businessUrl/:staffUrl/tip" element={<ProtectedComponent><TipProvider><TipPage /></TipProvider></ProtectedComponent>} />
            <Route path="/:businessUrl/tip" element={<ProtectedComponent><TipProvider><TipPage /></TipProvider></ProtectedComponent>} />

            <Route
                path="/embed/:businessUrl"
                element={<CalendarProvider><EmbeddedBooking embedded={true} /></CalendarProvider>}
            />
            <Route
                path="/biz/approve/:token"
                element={
                    <UpdateStaffStatus />
                }
            />
            <Route
                path="/search"
                element={
                    <SearchPage />
                }
            />
            <Route
                path="/:businessUrl"
                element={
                    <BusinessBookingTypes />
                }
            />

            <Route
                path="/:businessUrl/booking/:bookingTypeLink"
                element=
                {
                    <BookingTypePage
                    />
                }
            />

            <Route
                path="/:businessUrl/:staffUrl"
                element={
                    <ValidateBusinessUrl>
                        <UserBookingTypePage />
                    </ValidateBusinessUrl>
                }
            />



            <Route
                path="/:businessUrl/:staffUrl/:bookingTypeLink"
                element={
                    <BookingTypePage
                    />
                }
            />
            <Route path="biz/pricing" element={
                <PayPalScriptProvider options={{
                    "client-id": clientId,
                    vault: true,
                    intent: "subscription"
                }}>
                    <PricingPage />
                </PayPalScriptProvider>
            } />

            <Route path="/coins/buy" element={
                <PayPalScriptProvider options={{
                    "client-id": clientId,
                }}>
                    <ProtectedComponentNeedToHaveBusiness>
                        <TipProvider><BuyCoinsPage /></TipProvider>
                    </ProtectedComponentNeedToHaveBusiness>
                </PayPalScriptProvider>
            } />

            <Route
                path="/:businessUrl/:staffUrl/:bookingTypeLink/:time"
                element={
                    <PayPalScriptProvider options={{
                        "client-id": clientId,
                        vault: true,
                        intent: "subscription"
                    }}>
                        <BookingPage />
                    </PayPalScriptProvider>
                } />


            <Route
                path="/:businessUrl/booking/servicesConfirmed/:title/:timeRange/:time/:color/:staffUrl/:userName"
                element={
                    <ConfirmPage />}

            />

            <Route
                path="/signup"
                element={
                    !token ? (
                        <SignUp login={login} />
                    ) : (
                        <Navigate to="/biz/bookingtypes" />
                    )
                }
            />
            <Route path="/about" element={<PrivacyTermsAbout />} />
            <Route path="/privacy" element={<PrivacyTermsAbout />} />
            <Route path="/terms" element={<PrivacyTermsAbout />} />
            <Route path="/faq" element={<PrivacyTermsAbout />} />
            <Route
                path="/biz/integrations/:integration"
                element={
                    <ProtectedComponent>
                        <Main
                            content={IntegrationHeader}
                            display={Integration}
                            data={memoizedBookingTypes}

                        /></ProtectedComponent>
                }
            />
            <Route
                path="/biz/bookingtypes"
                element={
                    <ProtectedComponent><Main
                        content={BookingTypeHomePortal}
                        data={memoizedBookingTypes}
                        refetch={myBookingTypesDataRefetch}
                    /></ProtectedComponent>
                }

            />
            <Route
                path="/profile"
                element={<UserPage
                    logout={logout}
                    token={token}
                />
                }
            />
            <Route
                path="/myschedule"
                element={<CalendarProvider><ScheduledBookingType /></CalendarProvider>
                }
            />
            <Route
                path="/biz/profile"
                element={<ProtectedComponent><UserProfilePage
                    logout={logout}
                    token={token}
                /></ProtectedComponent>
                }
            />

            <Route
                path="biz/subscription-success/:planName"
                element={
                    <ProtectedComponent><SubscriptionSuccessPage /></ProtectedComponent>
                }
            />
            <Route
                path="/biz/staff"
                element={
                    <ProtectedComponent>
                        <StaffPortal /></ProtectedComponent>
                }
            />
            <Route
                path="/biz/integrations"
                element={<ProtectedComponent>
                    <Main
                        content={IntegrationHeader}
                        display={IntegrationPage}
                        data={memoizedBookingTypes}

                    />
                </ProtectedComponent>
                }
            />

            <Route
                path="/biz/create/:bookingtypecategory"
                element={<ProtectedComponent>
                    <Main
                        content={CreateNewDashBoard}
                        display={BookingTypeEntryForm}
                        data={memoizedBookingTypes}
                    />
                </ProtectedComponent>
                }
            />
            <Route
                path="/biz/update/:id"
                element={<ProtectedComponent>
                    <Main
                        content={CreateNewDashBoard}
                        display={BookingTypeEntryForm}
                        data={memoizedBookingTypes}
                        setImage={setImage}
                    />
                </ProtectedComponent>
                }
            />
            <Route path="*" element={<NotFound />} />

        </Routes>
    ), [logout, clientId, token, login, postLoginRedirect, memoizedBookingTypes, myBookingTypesDataRefetch, setImage]);
    // Public routes
    const BusinessRoutes = useMemo(() => {
        // If loading is true, it should always return the loading component
        if (hasLoading) {
            return <>{renderLoading()}</>; // Return loading component and stop further execution
        }

        // If there is an error, return the error component
        if (hasError) {
            return <>{renderError()}</>; // Return error component
        }

        // Finally, return the routes if there is no loading or error
        return <>{routes}</>;
    }, [hasLoading, hasError, routes]); // Ensure 'routes' is a dependency
    return (
        <>
            {BusinessRoutes}
        </>
    );

};

export default RouteGroups;